import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConversationMediaService } from "@/hooks/conversation/ConversationMediaService";

type ImageAttachmentForPersist = {
  id: string;
  file: File;
  analysisText?: string;
  compressedBase64?: string | null;
};

type QueuedImage = ImageAttachmentForPersist & {
  conversationId: string | null;
  status: "pending" | "uploading" | "failed";
};

export const useConversationImageMediaSaver = (params: {
  conversationId: string | null;
  userId?: string | null;
}) => {
  const { conversationId, userId = null } = params;

  const queueRef = useRef<QueuedImage[]>([]);
  const workerRef = useRef<() => Promise<void>>(async () => undefined);
  const processingRef = useRef(false);
  const idleWaitersRef = useRef<Array<() => void>>([]);
  const [queue, setQueue] = useState<QueuedImage[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const publishQueue = useCallback(() => setQueue([...queueRef.current]), []);
  const resolveIdleWaiters = useCallback(() => {
    if (processingRef.current || queueRef.current.some((item) => item.status !== "failed")) return;
    idleWaitersRef.current.splice(0).forEach((resolve) => resolve());
  }, []);

  const flushQueue = useCallback(async () => {
    if (processingRef.current) return;
    const queuedPending = queueRef.current.find((item) => item.status === "pending");
    if (!queuedPending || (!queuedPending.conversationId && !conversationId)) return;
    processingRef.current = true;
    setIsSaving(true);

    try {
      let firstPending = queueRef.current.find((item) => item.status === "pending");
      while (firstPending) {
        const targetConversationId = firstPending.conversationId ?? conversationId;
        if (!targetConversationId) return;
        const pendingBatch = queueRef.current.filter(
          (item) => item.status === "pending" && (item.conversationId ?? targetConversationId) === targetConversationId,
        );
        const batchIds = new Set(pendingBatch.map((item) => item.id));
        queueRef.current = queueRef.current.map((item) =>
          batchIds.has(item.id) ? { ...item, conversationId: targetConversationId, status: "uploading" } : item,
        );
        publishQueue();

        let results: boolean[];
        try {
          ({ results } = await ConversationMediaService.saveConversationImageMediasWithResults({
            conversationId: targetConversationId,
            userId,
            attachments: pendingBatch,
          }));
        } catch (error) {
          console.error("Failed to save conversation images:", error);
          results = pendingBatch.map(() => false);
        }
        const succeededIds = new Set(
          pendingBatch.filter((_, index) => results[index] === true).map((item) => item.id),
        );
        queueRef.current = queueRef.current
          .filter((item) => !succeededIds.has(item.id))
          .map((item) =>
            batchIds.has(item.id) && item.status === "uploading" ? { ...item, status: "failed" } : item,
          );
        publishQueue();
        firstPending = queueRef.current.find((item) => item.status === "pending");
      }
    } finally {
      processingRef.current = false;
      setIsSaving(false);
      resolveIdleWaiters();
      if (queueRef.current.some((item) => item.status === "pending")) void workerRef.current();
    }
  }, [conversationId, publishQueue, resolveIdleWaiters, userId]);

  workerRef.current = flushQueue;

  useEffect(() => {
    void flushQueue();
  }, [conversationId, flushQueue]);

  const stageImages = useCallback((attachments: ImageAttachmentForPersist[], targetConversationId?: string | null) => {
    if (!attachments || attachments.length === 0) return;
    const queuedIds = new Set(queueRef.current.map((item) => item.id));
    const next = attachments
      .filter((attachment) => !queuedIds.has(attachment.id))
      .map((attachment): QueuedImage => ({
        ...attachment,
        conversationId: targetConversationId ?? conversationId,
        status: "pending",
      }));
    if (next.length === 0) return;
    queueRef.current = [...queueRef.current, ...next];
    publishQueue();
    void workerRef.current();
  }, [conversationId, publishQueue]);

  const retryFailed = useCallback(() => {
    queueRef.current = queueRef.current.map((item) =>
      item.status === "failed" ? { ...item, status: "pending" } : item,
    );
    publishQueue();
    void workerRef.current();
  }, [publishQueue]);

  const waitForIdle = useCallback((timeoutMs = 10_000): Promise<boolean> => {
    if (!processingRef.current && !queueRef.current.some((item) => item.status !== "failed")) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      const onIdle = () => {
        clearTimeout(timeout);
        resolve(true);
      };
      const timeout = window.setTimeout(() => {
        idleWaitersRef.current = idleWaitersRef.current.filter((waiter) => waiter !== onIdle);
        resolve(false);
      }, timeoutMs);
      idleWaitersRef.current.push(onIdle);
    });
  }, []);

  const settleUploads = useCallback(async (timeoutMs = 10_000): Promise<boolean> => {
    const startedAt = Date.now();
    await waitForIdle(Math.max(1, Math.floor(timeoutMs / 2)));
    if (queueRef.current.some((item) => item.status === "failed")) {
      queueRef.current = queueRef.current.map((item) =>
        item.status === "failed" ? { ...item, status: "pending" } : item,
      );
      publishQueue();
      void workerRef.current();
    }
    const remaining = Math.max(1, timeoutMs - (Date.now() - startedAt));
    await waitForIdle(remaining);
    return !processingRef.current && queueRef.current.length === 0;
  }, [publishQueue, waitForIdle]);

  const failedCount = useMemo(() => queue.filter((item) => item.status === "failed").length, [queue]);

  return {
    stageImages,
    isSaving,
    pendingCount: queue.length,
    failedCount,
    retryFailed,
    waitForIdle,
    settleUploads,
  };
};

